import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function QuizLeaderboard() {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch quiz info
        const quizResponse = await axios.get(`${BACKEND_URL}/api/quizzes/${quizId}`);
        setQuiz(quizResponse.data);

        // Fetch leaderboard
        const leaderboardResponse = await axios.get(`${BACKEND_URL}/api/leaderboard/quiz/${quizId}`);
        setLeaderboard(leaderboardResponse.data);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [quizId]);

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}°`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('es-ES', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Cargando ranking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900" data-testid="quiz-leaderboard">
      <header className="bg-white bg-opacity-10 backdrop-blur-md shadow-lg border-b border-white border-opacity-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-white">🏆 Top 10 Ranking</h1>
              {quiz && <p className="text-purple-200 mt-1">{quiz.title}</p>}
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition"
              data-testid="back-btn"
            >
              ← Volver
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {leaderboard.length === 0 ? (
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-12 text-center">
            <div className="text-6xl mb-4">📊</div>
            <h3 className="text-2xl font-semibold text-white mb-2">No hay puntuaciones aún</h3>
            <p className="text-purple-200">¡Sé el primero en completar este quiz!</p>
          </div>
        ) : (
          <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6 shadow-2xl">
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div
                  key={index}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                    entry.rank === 1
                      ? 'bg-yellow-500 bg-opacity-30 ring-2 ring-yellow-400'
                      : entry.rank === 2
                      ? 'bg-gray-300 bg-opacity-30 ring-2 ring-gray-400'
                      : entry.rank === 3
                      ? 'bg-orange-500 bg-opacity-30 ring-2 ring-orange-400'
                      : 'bg-white bg-opacity-10'
                  }`}
                  data-testid={`leaderboard-entry-${index}`}
                >
                  {/* Rank */}
                  <div className="w-16 text-center">
                    <span className={`text-3xl font-bold ${
                      entry.rank <= 3 ? 'text-white' : 'text-purple-200'
                    }`}>
                      {getMedalEmoji(entry.rank)}
                    </span>
                  </div>

                  {/* User Picture */}
                  {entry.user_picture ? (
                    <img
                      src={entry.user_picture}
                      alt={entry.user_name}
                      className="w-14 h-14 rounded-full border-2 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-purple-600 flex items-center justify-center text-white text-xl font-bold border-2 border-white shadow-lg">
                      {entry.user_name.charAt(0).toUpperCase()}
                    </div>
                  )}

                  {/* User Info */}
                  <div className="flex-1">
                    <div className="font-bold text-white text-lg">{entry.user_name}</div>
                    {entry.date && (
                      <div className="text-sm text-purple-200">{formatDate(entry.date)}</div>
                    )}
                  </div>

                  {/* Score */}
                  <div className="text-right">
                    <div className="text-3xl font-bold text-white">{entry.score}</div>
                    <div className="text-xs text-purple-200">puntos</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quiz Stats */}
        {quiz && leaderboard.length > 0 && (
          <div className="mt-6 bg-white bg-opacity-10 backdrop-blur-lg rounded-xl p-6 text-white">
            <h3 className="text-lg font-semibold mb-4">📊 Estadísticas del Quiz</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-3xl font-bold text-yellow-300">{leaderboard[0]?.score || 0}</div>
                <div className="text-sm text-purple-200">Puntuación Máxima</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-300">{quiz.questions?.length || 0}</div>
                <div className="text-sm text-purple-200">Preguntas</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-300">{leaderboard.length}</div>
                <div className="text-sm text-purple-200">Participantes</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizLeaderboard;
